// Resilient client-side error logging for diagnostics (capture issues + any unhandled frontend error).
//
// WHY: in production some requests get dropped/aborted by the browser (mislabeled "CORS errors").
// Because a naive "POST the error to the backend" goes over the same network, those very errors are
// lost and Capture Health stays empty. This module persists every error to localStorage FIRST
// (survives a dropped request, a backgrounded tab, even a crash), then tries to send it. Buffered
// entries are flushed on page load and whenever the tab becomes visible again.
import { api } from "./api";

const KEY = "ww_client_logs";
const MAX = 80;                       // ring-buffer cap (entries are small text)
const MAX_AGE_MS = 24 * 3600 * 1000;  // drop buffered entries older than 24h
const DEDUP_MS = 8000;                // collapse identical bursts within this window
const recent = new Map();             // message -> last-sent ts (dedup)

function read() { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; } }
function write(list) { try { localStorage.setItem(KEY, JSON.stringify(list.slice(-MAX))); } catch { /* full/unavailable */ } }
function remove(id) { write(read().filter((e) => e.id !== id)); }

async function send(entry) {
    // Short timeout so a hung request fails fast and the entry simply stays buffered for the next flush.
    await api.post("/monitor/client-log", { message: entry.message, context: entry.context }, { timeout: 12000 });
}

// Record an error: dedup → persist locally → try to send. Auto-captures whether the tab was
// backgrounded / offline + the current path — the key signals for diagnosing a dropped request.
export function clientLog(message, context = {}) {
    const msg = String(message).slice(0, 1000);
    const now = Date.now();
    const last = recent.get(msg);
    if (last && now - last < DEDUP_MS) return;
    recent.set(msg, now);
    if (recent.size > 200) for (const [k, t] of recent) if (now - t > DEDUP_MS) recent.delete(k);
    const entry = {
        id: `${now}-${Math.random().toString(36).slice(2, 8)}`,
        message: msg,
        context: {
            online: typeof navigator !== "undefined" ? navigator.onLine : null,
            hidden: typeof document !== "undefined" ? document.hidden : null,
            path: typeof location !== "undefined" ? location.pathname : null,
            at: new Date().toISOString(),
            ...context,
        },
        ts: now,
    };
    const list = read(); list.push(entry); write(list);
    send(entry).then(() => remove(entry.id)).catch(() => { /* stays buffered for flush */ });
}

// Flush any locally-buffered logs (load + tab-visible). Removes each entry individually as it
// succeeds so logs added concurrently are never clobbered.
export async function flushClientLogs() {
    const list = read();
    if (!list.length) return;
    for (const entry of list) {
        if (Date.now() - entry.ts > MAX_AGE_MS) { remove(entry.id); continue; }
        try { await send(entry); remove(entry.id); } catch { /* keep for next flush */ }
    }
}

let installed = false;
// Install app-wide capture of unhandled JS errors + promise rejections, and flush buffered logs on
// load + when the tab becomes visible. Call once at app start.
export function installClientLogging() {
    if (installed || typeof window === "undefined") return;
    installed = true;
    window.addEventListener("error", (e) => {
        const where = e?.filename ? ` @ ${e.filename}:${e.lineno || 0}:${e.colno || 0}` : "";
        clientLog(`window.error: ${e?.message || "script error"}${where}`, { kind: "window.error" });
    });
    window.addEventListener("unhandledrejection", (e) => {
        const r = e?.reason;
        const m = (r && (r.message || (typeof r === "string" ? r : r.toString?.()))) || "unhandledrejection";
        clientLog(`unhandledrejection: ${String(m).slice(0, 400)}`, { kind: "unhandledrejection" });
    });
    flushClientLogs();
    document.addEventListener("visibilitychange", () => { if (!document.hidden) flushClientLogs(); });
}
